import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRight, Download, Sparkles, SquareKanban, Users, Zap } from "lucide-react";
import { fromJS } from "immutable";
import { useQuery } from "@tanstack/react-query";
import { format, isValid, subMonths } from "date-fns";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import BillingAction from "../actions/BillingAction";
import SelectPlanModal from "./component/SelectPlanModal";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import { billingSetFreePlan, getBillingInvoices } from "../api/BillingApi";
import LayoutAction from "../actions/LayoutAction";
import GridTable from "../grid-v2/GridTable";
import { formatAmount } from "../api/DataTypeUtils";
import BillingAddress from "./component/BillingAddress";
import PaymentCard from "./component/PaymentCard";
import TenantAction from "../actions/TenantAction";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import ProjectAction from "../actions/ProjectAction";
import { useQuery as useQueryParams } from "../AppLayout";

function downloadImage(url) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const BillingSetting = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const showChangePlan = useQueryParams().get("showChangePlan") || "false";

  const tokenUsage =
    useSelector((state: any) => state.billingReducer.get("tokenUsage")) || fromJS({});
  const userLimit =
    useSelector((state: any) => state.billingReducer.get("userLimit")) || fromJS({});
  const subscriptionStatus =
    useSelector((state: any) => state.billingReducer.get("subscriptionStatus")) || fromJS({});
  const plans = useSelector((state: any) => state.billingReducer.get("plans")) || fromJS([]);
  const teamPipelines =
    useSelector((state: any) => state.tenant.get("teamPipelines")) || fromJS([]);
  const teamMembers = useSelector((state: any) => state.tenant.get("teamMembers")) || fromJS([]);
  const automationUsage = useSelector((state: any) => state.projectReducer.get("automationUsage"));

  const currentPlanKey = subscriptionStatus.get("planKey");
  const currentPeriodStart = subscriptionStatus.get("currentPeriodStart");
  const currentPeriodStartDateTime =
    currentPeriodStart && isValid(new Date(currentPeriodStart * 1000))
      ? new Date(currentPeriodStart * 1000)
      : new Date();
  const startPlan = format(subMonths(currentPeriodStartDateTime, 1), "LLL do, yyyy");

  const currentPeriodEnd = subscriptionStatus.get("currentPeriodEnd");
  const currentPeriodEndDateTime =
    currentPeriodEnd && isValid(new Date(currentPeriodEnd * 1000))
      ? new Date(currentPeriodEnd * 1000)
      : new Date();
  const endPlan = format(subMonths(currentPeriodEndDateTime, 1), "LLL do, yyyy");

  const planMonney = formatAmount(
    i18n.language,
    subscriptionStatus.get("amount") || 0,
    subscriptionStatus.get("currency")
  );

  const totalTokens = tokenUsage.get("totalTokens") || 0;
  const currentAiContentTokensPerMonth =
    userLimit.getIn(["entitlements", "aiContentTokensPerMonth"]) || 0;
  const hasAiTokenAllowance = currentAiContentTokensPerMonth > 0;
  let totalTokenUsed = hasAiTokenAllowance
    ? `${totalTokens}/${currentAiContentTokensPerMonth}`
    : `${totalTokens} / 0`;
  let percentTokenUsed = hasAiTokenAllowance
    ? `${Math.min(100, (totalTokens / currentAiContentTokensPerMonth) * 100)}%`
    : "0%";

  const totalMembers = teamMembers.size;
  const maxUsersPerTenant = userLimit.getIn(["entitlements", "maxUsersPerTenant"]) || 0;
  const totalMemberUsed = `${totalMembers}/${maxUsersPerTenant}`;
  const percentMemberUsed = `${(totalMembers / maxUsersPerTenant) * 100}%`;

  const totalPipelines = teamPipelines.size;
  const maxPipelines = userLimit.getIn(["entitlements", "maxPipelines"]) || 0;
  const totalPipelineUsed = `${totalPipelines}/${maxPipelines}`;
  const percentPipelineUsed = `${(totalPipelines / maxPipelines) * 100}%`;

  const currentPlan = plans.find((item) => item.get("key") === currentPlanKey) || fromJS({});

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const billingInvoiceQuery = useQuery({
    queryKey: ["billing-invoices"],
    queryFn: () => getBillingInvoices(),
  });

  const invoiceHeaders = [
    { key: "number", title: "invoice number" },
    { key: "amountPaid", title: "total (tax incl.)" },
    { key: "created", title: "created date" },
    { key: "status", title: "status" },
    { key: "action", title: "" },
  ];

  useEffect(() => {
    dispatch(TenantAction.getTeamMembers());
  }, [dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(BillingAction.getBillingTokenUsage(projectId));
      dispatch(ProjectAction.getWorkspaceAutomationUsage(projectId));
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (showChangePlan === "true") {
      navigate(`/workspaces/${projectId}/settings/team?item=billing&key=Team%20Settings`);
      dispatch(BillingAction.showSelectPlanDialog());
    }
  }, [showChangePlan, projectId, dispatch, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const filterData = useMemo(() => {
    const invoices = billingInvoiceQuery.data || [];
    let datas = invoices.sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
    if (debouncedSearchText) {
      datas = datas.filter(
        (item) =>
          (item.number &&
            item.number.trim().toLowerCase().includes(debouncedSearchText.trim().toLowerCase())) ||
          (item.status &&
            item.status.trim().toLowerCase().includes(debouncedSearchText.trim().toLowerCase()))
      );
    }
    return datas.map((item) => {
      const amount = (item.amountPaid || 0) / 100;
      return {
        number: (
          <div
            onClick={() => {
              window.open(item.hostedInvoiceUrl, "_blank");
            }}
            className="text-blue-600 cursor-pointer hover:underline"
          >
            {item.number}
          </div>
        ),
        amountPaid: (
          <div className="uppercase">{`${formatAmount(i18n.language, amount, item.currency, "-")}`}</div>
        ),
        created: item.created ? format(item.created * 1000, "yyyy-MM-dd HH:mm") : null,
        status: (
          <div
            className={`capitalize w-max rounded-full border border-gray-200 py-1 px-2 ${
              item.status === "paid" ? "text-white bg-teal-500" : "text-white bg-yellow-500"
            }`}
          >
            {`${item.status}`}
          </div>
        ),
        action: (
          <button
            onClick={() => {
              downloadImage(item.invoicePdf);
            }}
            className="flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
          >
            <Download className="w-4 h-4 mr-2" />
            {t("Download")}
          </button>
        ),
      };
    });
  }, [billingInvoiceQuery.data, debouncedSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelSubscription = () => {
    setShowConfirm({
      show: true,
      title: t("Cancel Subscription"),
      description: (
        <div>
          <div>
            {t(
              "Are you sure you want to cancel your current subscription and set up the free plan?"
            )}
          </div>
          <br />
          <div className="font-bold">
            {t(
              "Your current plan will be used until the end of the month and automatically switched to the free plan."
            )}
          </div>
        </div>
      ),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "SET_FREE_PLAN",
    });
  };

  const handleUpgradePlan = () => {
    dispatch(BillingAction.showSelectPlanDialog());
  };

  const handleSubmit = () => {
    if (showConfirm.type === "SET_FREE_PLAN") {
      billingSetFreePlan("FREE_1_USER")
        .then(() => {
          dispatch(BillingAction.getBillingUserLimits());
          dispatch(BillingAction.getBillingSubscriptionStatus());
          setShowConfirm({ show: false, title: "", description: "" });
          dispatch(
            LayoutAction.setNotification({
              title: t("Cancel Subscription"),
              type: "success",
              message: t("Updated free plan"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Cancel Subscription"),
              type: "error",
              message: t("Failed to cancel subscription"),
            })
          );
        });
    }
  };

  const getLimitUserText = (plan) => {
    const automationLimit = Number(plan?.entitlements?.maxAutomationExecutionsPerMonth);
    const automationText =
      !Number.isFinite(automationLimit) || automationLimit >= 2147483647
        ? t("unlimited automation runs/month")
        : t("up to {{count}} automation runs/month", { count: automationLimit.toLocaleString() });
    if (plan.key === "ENTERPRISE_UNLIMITED") {
      return `${planMonney} per team, billed monthly, ${t("unlimited users")}, ${automationText}`;
    } else {
      return `${planMonney} per team, billed monthly, max ${plan.maxUsers || 0} users per team, ${automationText}`;
    }
  };

  const automationLimit = automationUsage?.get("limit");
  const automationExecuted = automationUsage?.get("executedCount") || 0;
  const automationUsageText =
    automationLimit == null
      ? `${automationExecuted}/${t("Unlimited")}`
      : `${automationExecuted}/${automationLimit}`;
  const automationPercentUsed =
    automationLimit && automationLimit > 0
      ? `${Math.min(100, (automationExecuted / automationLimit) * 100)}%`
      : "0%";

  return (
    <div className="py-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Plan & Billing")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Update your payment information or switch plans according to your needs")}
        </p>
      </div>
      <div className="py-8">
        <h2 className="text-sm font-semibold mb-1">{t("Current Plan")}</h2>
        {currentPlanKey !== "FREE_1_USER" ? (
          <p className="text-sm/6 text-gray-500 mb-3">
            <span>{t("Start from")}</span>{" "}
            <span className="font-semibold text-gray-900">{startPlan}</span> <span>{t("to")}</span>{" "}
            <span className="font-semibold text-gray-900">{endPlan}</span>
          </p>
        ) : null}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl flex justify-between items-center">
          <div className="flex items-center">
            <div className="p-3 mr-2 rounded-xl bg-blue-600">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                {currentPlan.get("name") || t("Plan")}
              </h3>
              <p className="text-sm text-gray-600">{getLimitUserText(currentPlan.toJS())}</p>
            </div>
          </div>
          <button
            onClick={handleUpgradePlan}
            className="text-blue-600 font-medium hover:underline cursor-pointer"
          >
            {t("Change Plan")}
          </button>
        </div>
      </div>
      <div className="pb-10">
        <h2 className="text-sm font-semibold mb-1">{t("Usage")}</h2>
        <p className="text-sm/6 text-gray-500 mb-3">
          {t("Manage your members, pipelines, and credits")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-red-500" />
              {t("Members")}
            </h3>
            <p className="text-sm mb-3">{totalMemberUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-500" style={{ width: percentMemberUsed }}></div>
            </div>
            <button
              onClick={() => {
                navigate(`/workspaces/${projectId}/settings/team?item=member&key=Team%20Settings`);
              }}
              className="mt-4 flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              {t("Manage members")}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <SquareKanban className="w-4 h-4 text-teal-500" />
              {t("Pipelines")}
            </h3>
            <p className="text-sm mb-3">{totalPipelineUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500" style={{ width: percentPipelineUsed }}></div>
            </div>
            <button
              onClick={() => {
                navigate(
                  `/workspaces/${projectId}/settings/team?item=pipeline&key=Team%20Settings`
                );
              }}
              className="mt-4 flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              {t("Manage pipelines")}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              {t("AI Credits")}
            </h3>
            <p className="text-sm mb-3">{totalTokenUsed}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-500" style={{ width: percentTokenUsed }}></div>
            </div>
            <button
              onClick={() => {
                dispatch(BillingAction.showSelectPlanDialog());
              }}
              className="mt-4 flex items-center py-1 px-3 text-sm text-gray-700 font-medium bg-white border border-gray-300 rounded-xl cursor-pointer"
            >
              {t("Add more credit")}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="p-5 bg-gray-100 rounded-xl">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-500" />
              {t("Automation Runs")}
            </h3>
            <p className="text-sm mb-3">{automationUsageText}</p>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: automationPercentUsed }}></div>
            </div>
          </div>
        </div>
      </div>
      <div className="pb-10">
        <h2 className="text-sm font-semibold mb-1">{t("Billing")}</h2>
        <p className="text-sm/6 text-gray-500 mb-3">
          {t("Manage your payment methods and billing information")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BillingAddress />
          <PaymentCard />
        </div>
      </div>
      <div className="pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold mb-1">{t("Invoices")}</h2>
            <p className="text-sm/6 text-gray-500 mb-3">
              {t("View and track your past invoices and payment history")}
            </p>
          </div>
          <div className="flex">
            <div className="relative w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder={t("Number")}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-600 text-sm font-medium placeholder-gray-500"
                onChange={(e) => {
                  const newVal = e.target.value || "";
                  setSearchText(newVal);
                }}
              />
            </div>
          </div>
        </div>
        <GridTable headers={invoiceHeaders} rows={filterData} pageSize={10} />
      </div>
      {currentPlanKey !== "FREE_1_USER" ? (
        <div>
          <button
            onClick={handleCancelSubscription}
            className="border border-gray-300 bg-red-500 text-white rounded-lg px-3 py-1.5 font-medium hover:bg-red-400"
          >
            {t("Cancel subscription")}
          </button>
        </div>
      ) : null}
      <SelectPlanModal />
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default BillingSetting;
